import { getallsupplier, fetchShipFroms } from "../../../services/supplier/supplier";
import { normalizeStorer } from "../../../components/lookupConfigs";
import { formatToInputDate } from "../../../utils/dateFormatter";

const text = (v) => {
  if (v === undefined || v === null) return "";
  return String(v).trim();
};

const firstText = (...vals) => {
  for (const v of vals) {
    const t = text(v);
    if (t) return t;
  }
  return "";
};

const firstValue = (...vals) => {
  for (const v of vals) {
    if (v === undefined || v === null || v === "") continue;
    return v;
  }
  return "";
};

const asDate = (v) => (v ? formatToInputDate(v, false) || "" : "");

const PAYMENT_TERMS = ["30 Days", "45 Days", "60 Days", "Prepaid", "On Delivery"];

const matchPaymentTerms = (value) => {
  const raw = text(value);
  if (!raw) return "";
  return PAYMENT_TERMS.find((opt) => opt.toLowerCase() === raw.toLowerCase()) || raw;
};

const isObjectId = (v) => /^[a-f\d]{24}$/i.test(text(v));

const orgId = (v) => {
  if (!v) return "";
  if (typeof v === "object") return text(v._id || v.id);
  return text(v);
};

/** Supplier key stored on the PO, used to call `supplier/fetch-multi-dynamic`. */
export function extractPoSupplierId(po) {
  if (!po || typeof po !== "object") return "";
  const supplier = po.supplier || {};
  const addr = supplier.address || {};
  const hdr = po.purchase_order || {};
  return firstText(
    supplier.supplierId,
    supplier.supplier_id,
    orgId(supplier.organizationCustomerId),
    po.supplierId,
    hdr.supplierId,
    addr.supplierId,
    addr.sellersReference,
    hdr.supplierReference,
    supplier.supplier,
    hdr.sellersReference
  );
}

function firstPoDocument(blocks) {
  for (const block of blocks || []) {
    if (Array.isArray(block?.poDetails) && block.poDetails.length) return block.poDetails[0];
    const fromItem = (block?.items || []).find(
      (item) => Array.isArray(item?.poDetails) && item.poDetails.length
    );
    if (fromItem) return fromItem.poDetails[0];
  }
  return null;
}

function allItems(blocks) {
  return (blocks || []).flatMap((block) => (Array.isArray(block?.items) ? block.items : []));
}

function sumItems(items, key) {
  return items.reduce((total, item) => total + (Number(item?.[key]) || 0), 0);
}

function udfValue(udf, ...keys) {
  for (const key of keys) {
    const value = udf?.[key];
    if (value !== undefined && value !== null && text(value) !== "") return value;
  }
  return "";
}

function supplierAddressPatch(supplier, hdr) {
  const addr = supplier?.address || {};
  return {
    supplierId: firstText(supplier?.supplierId, supplier?.supplier_id, addr.supplierId),
    supplierName: firstText(supplier?.supplier, supplier?.supplierName, supplier?.name),
    supplierAddressLine1: firstText(addr.addressLine1, addr.supplierAddressLine1),
    supplierAddressLine2: firstText(addr.addressLine2, addr.supplierAddressLine2),
    supplierAddressLine3: firstText(addr.addressLine3, addr.supplierAddressLine3),
    supplierAddressLine4: firstText(addr.addressLine4, addr.supplierAddressLine4),
    supplierCity: firstText(addr.city, addr.supplierCity),
    supplierState: firstText(addr.state, addr.supplierState),
    supplierCountry: firstText(addr.country, addr.supplierCountry),
    supplierReference: firstText(addr.sellersReference, hdr?.sellersReference, hdr?.supplierReference),
    supplierContact: firstText(addr.contact, addr.phone, supplier?.contact),
    supplierEmail: firstText(addr.email, supplier?.email),
  };
}

function buyerAddressPatch(buyer, hdr) {
  const addr = buyer?.address || {};
  return {
    buyerName: firstText(buyer?.buyer, buyer?.buyerName, hdr?.buyerName),
    buyerAddressLine1: firstText(addr.addressLine1),
    buyerAddressLine2: firstText(addr.addressLine2),
    buyerAddressLine3: firstText(addr.addressLine3),
    buyerAddressLine4: firstText(addr.addressLine4),
    buyerCity: firstText(addr.city),
    buyerState: firstText(addr.state),
    buyerCountry: firstText(addr.country),
    buyerReference: firstText(addr.buyersReference, hdr?.buyersReference),
    buyerContact: firstText(addr.contact, addr.phone),
    buyerEmail: firstText(addr.email),
  };
}

/** Header, address, carrier, load, invoice, and UDF values taken from a get-po-multi block. */
export function mapPoBlocksToAsnPatch(blocks) {
  const po = firstPoDocument(blocks) || {};
  const hdr = po.purchase_order || {};
  const buyer = po.buyer || {};
  const supplier = po.supplier || {};
  const misc = po.misc || {};
  const loading = po.loading || {};
  const udf = po.udf || {};
  const items = allItems(blocks);
  const buyerAddr = buyer.address || {};

  const received = firstValue(hdr.totalReceivedQty, items.length ? sumItems(items, "receivedQty") : "");
  const expected = firstValue(hdr.totalOrderedQuantity, items.length ? sumItems(items, "orderedQty") : "");
  const poPrice = firstValue(hdr.totalPOPrice, items.length ? sumItems(items, "totalPrice") : "");

  return {
    owner: firstText(hdr.buyerName, buyer.buyer),
    asnType: "PO",
    destinationLocationName: firstText(
      hdr.purchasingOfficeLocation,
      buyerAddr.city,
      hdr.warehouse,
      po.warehouse
    ),
    totalReceivedQty: received === "" ? "" : received,
    totalExpectedQty: expected === "" ? "" : expected,
    totalWeight: firstValue(hdr.totalWeight),
    totalVolume: firstValue(hdr.totalVolume),
    totalPOValue: poPrice === "" ? "" : poPrice,
    closedDate: asDate(hdr.closedDate),

    ...supplierAddressPatch(supplier, hdr),
    ...buyerAddressPatch(buyer, hdr),

    carrierName: firstText(loading.carrier, hdr.carrier),
    expectedArrivalDate: asDate(hdr.expectedReceiptDate || loading.expectedArrivalDate),
    paymentTerms: matchPaymentTerms(
      firstText(loading.termsOfPayment, misc.termsOfPayment, hdr.termsOfPayment)
    ),

    asnReceiptTextUdf1: firstText(
      udfValue(udf, "textUDF1", "purchaseOrderTextUDF1"),
      misc.poHeaderUDF1,
      misc.poHeaderUdf1
    ),
    asnReceiptTextUdf2: firstText(
      udfValue(udf, "textUDF2", "purchaseOrderTextUDF2"),
      misc.poHeaderUDF2,
      misc.poHeaderUdf2
    ),
    asnReceiptTextUdf3: firstText(
      udfValue(udf, "textUDF3", "purchaseOrderTextUDF3"),
      misc.poHeaderUDF3,
      misc.poHeaderUdf3,
      misc.notes
    ),
    asnReceiptNumericUdf1: firstValue(udfValue(udf, "numericUDF1", "purchaseOrderNumericUDF1")),
    asnReceiptNumericUdf2: firstValue(udfValue(udf, "numericUDF2", "purchaseOrderNumericUDF2")),
    asnReceiptNumericUdf3: firstValue(udfValue(udf, "numericUDF3", "purchaseOrderNumericUDF3")),
    asnReceiptDateUdf1: asDate(udfValue(udf, "dateUDF1", "purchaseOrderDateUDF1")),
    asnReceiptDateUdf2: asDate(udfValue(udf, "dateUDF2", "purchaseOrderDateUDF2")),
    asnReceiptDateUdf3: asDate(udfValue(udf, "dateUDF3", "purchaseOrderDateUDF3")),
  };
}

function addressFromStorer(rec, prefix) {
  const row = normalizeStorer(rec, prefix);
  return {
    supplierName: row.name || "",
    supplierAddressLine1: row.address1 || "",
    supplierAddressLine2: row.address2 || "",
    supplierAddressLine3: "",
    supplierAddressLine4: "",
    supplierCity: row.city || "",
    supplierState: row.state || "",
    supplierCountry: row.country || "",
    supplierReference: row.id || "",
    supplierContact: row.mobile || "",
    supplierEmail: row.email || "",
  };
}

function pickSupplier(rows, key) {
  const norm = text(key).toLowerCase();
  if (!norm) return null;
  return (
    (rows || []).find((row) =>
      [row?._id, row?.supplier_id, row?.supplierId, row?.supplier_name, row?.id]
        .some((value) => text(value).toLowerCase() === norm)
    ) || null
  );
}

async function findSupplier(key) {
  const attempts = isObjectId(key)
    ? [{ _id: key }, { supplierId: key }, { supplier_id: key }]
    : [{ supplier_id: key }, { supplierId: key }, { supplier_name: key }];

  for (const filters of attempts) {
    try {
      const res = await getallsupplier(0, 20, filters);
      const hit = pickSupplier(res?.data, key);
      if (hit) return hit;
    } catch (err) {
      console.warn("supplier/fetch-multi-dynamic lookup failed", filters, err);
    }
  }

  try {
    const res = await getallsupplier(0, 200, {});
    return pickSupplier(res?.data, key);
  } catch (err) {
    console.warn("supplier/fetch-multi-dynamic lookup failed", err);
    return null;
  }
}

function nestedShipFroms(supplier) {
  const nested =
    supplier?.shipFrom ||
    supplier?.shipfrom ||
    supplier?.shipFroms ||
    supplier?.shipfroms ||
    supplier?.ship_from;
  return Array.isArray(nested) ? nested : [];
}

/**
 * Resolve the PO supplier through `supplier/fetch-multi-dynamic`, then load that
 * supplier's ship-from list (`supplier/ship-from/fetch-multi-dynamic`).
 */
export async function loadShipFromForPoSupplier(supplierKey) {
  const key = text(supplierKey);
  if (!key) return { supplier: null, shipFroms: [], supplierId: "" };

  const supplier = await findSupplier(key);
  const supplierId = text(supplier?._id) || (isObjectId(key) ? key : "");
  let shipFroms = nestedShipFroms(supplier);

  if (!shipFroms.length && supplierId) {
    const res = await fetchShipFroms(0, 50, { supplierId });
    shipFroms = Array.isArray(res?.data) ? res.data : [];
  }

  return { supplier, shipFroms, supplierId };
}

function applyPatch(onChange, map) {
  Object.entries(map || {}).forEach(([name, value]) => {
    onChange({ target: { name, value: value ?? "" } });
  });
}

/**
 * Copy the PO onto the ASN form, then replace Ship From with the supplier's
 * ship-from list (`supplier/fetch-multi-dynamic` → ship-from list).
 * Returns how many ship-from rows were found.
 */
export async function prefillAsnFromPurchaseOrder(blocks, onChange) {
  applyPatch(onChange, mapPoBlocksToAsnPatch(blocks));

  let supplierKey = "";
  for (const block of blocks || []) {
    const po =
      (Array.isArray(block?.poDetails) && block.poDetails[0]) ||
      (block?.items || []).find((item) => Array.isArray(item?.poDetails) && item.poDetails.length)?.poDetails?.[0];
    supplierKey = extractPoSupplierId(po);
    if (supplierKey) break;
  }
  if (!supplierKey) return { shipFromCount: 0 };

  const resolved = await loadShipFromForPoSupplier(supplierKey);
  const shipFromCount = Array.isArray(resolved.shipFroms) ? resolved.shipFroms.length : 0;

  if (shipFromCount > 0) {
    applyPatch(onChange, mapResolvedShipFromPatch(resolved));
  } else if (resolved.supplierId) {
    applyPatch(onChange, { poSupplierId: resolved.supplierId });
  }

  return { shipFromCount, supplierMissing: !resolved.supplier };
}

/** Ship-from row wins; supplier master fills the address when the list is empty. */
export function mapResolvedShipFromPatch({ supplier, shipFroms, supplierId }) {
  const fromList = Array.isArray(shipFroms) && shipFroms.length
    ? addressFromStorer(shipFroms[0], "shipfrom")
    : null;
  const fromSupplier = supplier ? addressFromStorer(supplier, "supplier") : null;
  const address = fromList || fromSupplier || {};
  return {
    ...address,
    supplierId: text(supplier?.supplier_id || supplier?.supplierId) || "",
    poSupplierId: supplierId || "",
  };
}
